import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AtSign,
  Bell,
  CheckCheck,
  FileSignature,
  MessageSquare,
  Reply,
  ShieldAlert,
  Upload,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import { TYPE_LABELS, titleFor } from './notificationText';
import Spinner from '../../components/ui/Spinner';
import { formatDate } from '../../utils/format';
import { getErrorMessage } from '../../lib/apiClient';
import { useAuthStore } from '../../store/authStore';
import {
  getPreferences,
  listNotifications,
  markAllRead,
  markAllSeen,
  markRead,
  notificationKeys,
  setPreference,
} from './notificationsApi';

/** Where a notification's target actually lives, given its polymorphic target_type. */
function routeFor(n) {
  switch (n.targetType) {
    case 'folder':
      return n.targetId ? `/folder/${n.targetId}` : '/files';
    case 'file':
      return n.targetId ? `/files/${n.targetId}` : '/files';
    case 'signature_document':
      return n.targetId ? `/signatures/${n.targetId}` : '/signatures';
    case 'file_request':
      return '/file-requests';
    case 'organization_member':
      return '/members';
    case 'secret':
      return '/messages/sent';
    default:
      return '/files';
  }
}

function iconFor(type) {
  if (type === 'mentioned') return AtSign;
  if (type === 'comment_reply') return Reply;
  if (type.startsWith('signature_')) return FileSignature;
  if (type === 'malware_detected') return ShieldAlert;
  if (type === 'file_request_received') return Upload;
  return MessageSquare;
}

function PreferencesPanel() {
  const queryClient = useQueryClient();
  const { data: prefs = [] } = useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: getPreferences,
  });

  const mutation = useMutation({
    mutationFn: setPreference,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() }),
    onError: (err) => toast.error(getErrorMessage(err, 'Could not update preference')),
  });

  const value = (type, channel) =>
    prefs.find((p) => p.type === type && p.channel === channel)?.enabled ?? false;

  const types = Object.keys(TYPE_LABELS);

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-100 p-4 shadow-sm">
      <h2 className="mb-3 text-sm font-semibold text-slate-700">Delivery preferences</h2>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-slate-400">
            <th className="pb-2 text-left font-medium">Notify me about</th>
            <th className="pb-2 text-center font-medium">In-app</th>
            <th className="pb-2 text-center font-medium">Email</th>
          </tr>
        </thead>
        <tbody>
          {types.map((type) => (
            <tr key={type} className="border-t border-slate-100">
              <td className="py-2 text-slate-600">{TYPE_LABELS[type]}</td>
              {['in_app', 'email'].map((channel) => (
                <td key={channel} className="py-2 text-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer accent-brand-600"
                    checked={value(type, channel)}
                    disabled={mutation.isPending}
                    onChange={(e) =>
                      mutation.mutate({ type, channel, enabled: e.target.checked })
                    }
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function NotificationsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setTenant = useAuthStore((s) => s.setTenant);

  const { data, isLoading } = useQuery({
    queryKey: notificationKeys.list(false),
    queryFn: () => listNotifications(),
  });
  const notifications = data?.data ?? [];

  // Opening the page clears the unread badge (marks everything seen).
  useEffect(() => {
    markAllSeen()
      .then(() => queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() }))
      .catch(() => {});
  }, [queryClient]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const readMutation = useMutation({
    mutationFn: markRead,
    onSuccess: invalidate,
    onError: (err) => toast.error(getErrorMessage(err, 'Could not update')),
  });
  const readAllMutation = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      invalidate();
      toast.success('All caught up');
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Could not update')),
  });

  // Open a notification: mark it read, switch to the workspace it happened
  // in (it may not be the currently active one), then land on its target.
  const openNotification = (n) => {
    if (!n.readAt) readMutation.mutate(n.id);
    if (n.orgId && n.workspaceId) {
      setTenant({ orgId: n.orgId, workspaceId: n.workspaceId });
    }
    navigate(routeFor(n));
  };

  const unread = notifications.filter((n) => !n.readAt).length;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-slate-800">
          <Bell className="h-5 w-5 text-brand-600" />
          Notifications
        </h1>
        <Button
          variant="secondary"
          size="sm"
          disabled={unread === 0 || readAllMutation.isPending}
          onClick={() => readAllMutation.mutate()}
        >
          <CheckCheck className="h-4 w-4" />
          Mark all read
        </Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100 shadow-sm">
        {isLoading ? (
          <div className="py-16">
            <Spinner />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-slate-400">
            <Bell className="h-10 w-10" />
            <p className="text-sm">Nothing here yet.</p>
          </div>
        ) : (
          <ul>
            {notifications.map((n) => {
              const Icon = iconFor(n.type);
              return (
                <li
                  key={n.id}
                  className={`flex items-start gap-3 border-b border-slate-100 ${
                    n.readAt ? '' : 'bg-brand-50/40'
                  }`}
                >
                  <button
                    onClick={() => openNotification(n)}
                    className="flex flex-1 items-start gap-3 px-4 py-3 text-left hover:bg-slate-50"
                  >
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-700">{titleFor(n)}</p>
                      {n.payload?.preview && (
                        <p className="truncate text-sm text-slate-500">“{n.payload.preview}”</p>
                      )}
                      <p className="mt-0.5 text-xs text-slate-400">{formatDate(n.createdAt)}</p>
                    </div>
                  </button>
                  {!n.readAt && (
                    <button
                      className="shrink-0 px-4 py-3 text-xs text-brand-600 hover:underline"
                      onClick={() => readMutation.mutate(n.id)}
                    >
                      Mark read
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <PreferencesPanel />
    </div>
  );
}
